import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
    AlertCircle, ArrowLeft, Check, ChevronDown, ChevronRight, ClipboardList,
    Info, Loader2, Plus, RefreshCw, Save, Trash2, X,
} from 'lucide-react';
import { useDownloadAttachment, useLoadHttpClientEmpty } from '../../../App.hooks';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type NomenclatureData = {
    Pattern: string;
    Mappings: Record<string, { Values: Record<string, string> }>;
};

/**
 * [Name]  = mapped token  — value looked up in Mappings[Name].Values
 * {Name}  = direct token  — Revit property value used as-is
 * anything else = literal text
 */
type PatternToken =
    | { type: 'mapped'; name: string }
    | { type: 'direct'; name: string }
    | { type: 'literal'; text: string };

/** All standards use type='Standard', distinguished by name. */
const DYNAMIC_DATA_TYPE = 'Standards';
const DYNAMIC_DATA_NAME = 'Nomenclature';

/* ------------------------------------------------------------------ */
/*  GraphQL                                                            */
/* ------------------------------------------------------------------ */

const GET_PROJECT_NOMENCLATURE_DATA = gql`
    query getProjectNomenclatureData($id: String) {
        Project(id: $id) {
            dynamicData { id name type data setData }
        }
    }
`;

const SET_PROJECT_DYNAMIC_DATA = gql`
    mutation setNomenclatureDynamicData($projectId: String, $data: DynamicDataInput) {
        Project(id: $projectId) { setDynamicData(data: $data) }
    }
`;

const GET_LIBRARY_PREVIEW_CONTENT = gql`
    query getNomenclaturePreviewContent($libraryId: String) {
        Library(id: $libraryId) {
            contents(first: 5, order: { updatedTime: DESC }) {
                nodes { id name currentVersionId }
            }
        }
    }
`;

const GET_VERSION_PREVIEW_DATA = gql`
    query getVersionPreviewData($ids: [String]) {
        ContentVersionList(where: { id: { in: $ids } }) {
            nodes { id category subCategory settings }
        }
    }
`;

/* ------------------------------------------------------------------ */
/*  Pattern utilities                                                  */
/* ------------------------------------------------------------------ */

function parsePattern(pattern: string): PatternToken[] {
    if (!pattern) return [];
    const tokens: PatternToken[] = [];
    const re = /(\[[^\]]+\]|\{[^}]+\})/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(pattern)) !== null) {
        if (match.index > last) {
            tokens.push({ type: 'literal', text: pattern.slice(last, match.index) });
        }
        const raw = match[1];
        if (raw.startsWith('[')) {
            tokens.push({ type: 'mapped', name: raw.slice(1, -1) });
        } else {
            tokens.push({ type: 'direct', name: raw.slice(1, -1) });
        }
        last = match.index + raw.length;
    }
    if (last < pattern.length) {
        tokens.push({ type: 'literal', text: pattern.slice(last) });
    }
    return tokens;
}

function getMappedTokenNames(pattern: string): string[] {
    return parsePattern(pattern)
        .filter((t): t is Extract<PatternToken, { type: 'mapped' }> => t.type === 'mapped')
        .map(t => t.name);
}

/** A segment of the resolved pattern output, tagged with how it was resolved. */
type PreviewSegment = { text: string; kind: 'literal' | 'mapped' | 'direct' | 'miss' };

/**
 * Apply a parsed pattern to a property map, returning the full output string
 * and per-segment metadata for colour-coded display.
 */
function applyNomenclature(
    tokens: PatternToken[],
    properties: Record<string, string>,
    mappings: NomenclatureData['Mappings'],
): { output: string; segments: PreviewSegment[] } {
    const segments: PreviewSegment[] = [];
    let output = '';
    for (const token of tokens) {
        if (token.type === 'literal') {
            output += token.text;
            segments.push({ text: token.text, kind: 'literal' });
        } else {
            // Case-insensitive property lookup
            const raw = Object.entries(properties).find(
                ([k]) => k.toLowerCase() === token.name.toLowerCase(),
            )?.[1];
            if (raw === undefined || raw === '') {
                const placeholder = `?${token.name}?`;
                output += placeholder;
                segments.push({ text: placeholder, kind: 'miss' });
            } else if (token.type === 'mapped') {
                const mapped = mappings[token.name]?.Values?.[raw];
                const value = mapped !== undefined ? mapped : raw;
                output += value;
                segments.push({ text: value, kind: mapped !== undefined ? 'mapped' : 'direct' });
            } else {
                output += raw;
                segments.push({ text: raw, kind: 'direct' });
            }
        }
    }
    return { output, segments };
}

/* ------------------------------------------------------------------ */
/*  Paste-from-Excel modal for mapping tables                         */
/* ------------------------------------------------------------------ */

type PasteEntry = { original: string; mapped: string; isOverwrite: boolean };

type MappingPasteModalProps = {
    tokenName: string;
    existingValues: Record<string, string>;
    onImport: (entries: PasteEntry[]) => void;
    onClose: () => void;
};

const MappingPasteModal = ({ tokenName, existingValues, onImport, onClose }: MappingPasteModalProps) => {
    const [text, setText] = useState('');

    const entries = useMemo((): PasteEntry[] => {
        if (!text.trim()) return [];
        return text
            .split(/\r?\n/)
            .map(line => line.split('\t'))
            .filter(cols => cols.length >= 2)
            .map(cols => ({ original: cols[0].trim(), mapped: cols[1].trim() }))
            .filter(r => r.original)
            .filter(r => !(r.original.toLowerCase() === 'original' && r.mapped.toLowerCase() === 'mapped'))
            .map(r => ({ ...r, isOverwrite: r.original in existingValues }));
    }, [text, existingValues]);

    const newCount = entries.filter(e => !e.isOverwrite).length;
    const overwriteCount = entries.filter(e => e.isOverwrite).length;

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                backgroundColor: 'rgba(0,0,0,0.45)',
                zIndex: 50,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="card"
                style={{
                    width: 540, maxHeight: '80vh',
                    display: 'flex', flexDirection: 'column',
                    border: '1px solid var(--border-default)',
                    boxShadow: 'var(--shadow-lg, 0 8px 30px rgba(0,0,0,.2))',
                }}
            >
                <div
                    className="flex items-center gap-2 px-4 py-3 border-b flex-shrink-0"
                    style={{ borderColor: 'var(--border-default)' }}
                >
                    <ClipboardList size={15} style={{ color: 'var(--accent)' }} />
                    <span className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                        Paste mappings for [{tokenName}]
                    </span>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 2 }}
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 flex flex-col gap-3 overflow-hidden flex-1">
                    <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        Copy two columns from Excel — <strong>Original</strong> and <strong>Mapped</strong> — then paste below.
                        A header row is detected and skipped automatically.
                    </p>
                    <textarea
                        autoFocus
                        className="input-base w-full"
                        style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical', minHeight: 110 }}
                        placeholder={'Original\tMapped\nSystem Panel\tTEST_System Panel'}
                        value={text}
                        onChange={e => setText(e.target.value)}
                    />

                    {entries.length > 0 && (
                        <div
                            className="overflow-auto flex-shrink-0"
                            style={{ maxHeight: 200, border: '1px solid var(--border-default)', borderRadius: 6 }}
                        >
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                    <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--surface-1, var(--surface-100))' }}>
                                        <th style={{ padding: '5px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Original</th>
                                        <th style={{ padding: '5px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Mapped To</th>
                                        <th style={{ padding: '5px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((e, i) => (
                                        <tr
                                            key={i}
                                            style={{
                                                borderTop: '1px solid var(--border-subtle)',
                                                backgroundColor: e.isOverwrite ? 'var(--warning-soft, #fff8e9)' : undefined,
                                            }}
                                        >
                                            <td style={{ padding: '4px 10px', color: 'var(--text-primary)' }}>{e.original}</td>
                                            <td style={{ padding: '4px 10px', color: 'var(--text-primary)' }}>{e.mapped}</td>
                                            <td style={{ padding: '4px 10px' }}>
                                                {e.isOverwrite
                                                    ? <span style={{ color: 'var(--warning, #b45309)' }}>Will overwrite</span>
                                                    : <span style={{ color: 'var(--accent)' }}>New</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {entries.length > 0 && (
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            {newCount} new{overwriteCount > 0 ? `, ${overwriteCount} will overwrite existing` : ''}.
                        </p>
                    )}
                </div>

                <div
                    className="flex items-center justify-end gap-2 px-4 py-3 border-t flex-shrink-0"
                    style={{ borderColor: 'var(--border-default)' }}
                >
                    <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
                    <button
                        className="btn-primary text-sm"
                        disabled={entries.length === 0}
                        style={{ opacity: entries.length === 0 ? 0.5 : 1 }}
                        onClick={() => { onImport(entries); onClose(); }}
                    >
                        Import {entries.length > 0 ? `${entries.length} row${entries.length !== 1 ? 's' : ''}` : ''}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

type NomenclatureStandardProps = {
    projectId: string;
    libraryId?: string;
    style?: CSSProperties;
    onBack: () => void;
};

type RawPreviewItem = { contentName: string; properties: Record<string, string> };

const NomenclatureStandard = ({ projectId, libraryId, style, onBack }: NomenclatureStandardProps) => {
    const [data, setData] = useState<NomenclatureData>({ Pattern: '', Mappings: {} });
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingBlob, setIsLoadingBlob] = useState(false);
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

    const [pasteModalFor, setPasteModalFor] = useState<string | null>(null);
    const [addingRowFor, setAddingRowFor] = useState<string | null>(null);
    const [newRowOriginal, setNewRowOriginal] = useState('');
    const [newRowMapped, setNewRowMapped] = useState('');
    const [editingCell, setEditingCell] = useState<{
        mapping: string; key: string; field: 'key' | 'value'; draft: string;
    } | null>(null);
    const [searchFor, setSearchFor] = useState<Record<string, string>>({});
    const [previewOpen, setPreviewOpen] = useState(false);
    const [rawPreviewItems, setRawPreviewItems] = useState<RawPreviewItem[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    const download = useDownloadAttachment();
    const httpClient = useLoadHttpClientEmpty();

    /* ── Query ──────────────────────────────────────────────────── */
    const { data: queryData, loading: queryLoading, refetch } = useQuery(
        GET_PROJECT_NOMENCLATURE_DATA,
        { variables: { id: projectId }, skip: !projectId, fetchPolicy: 'network-only' },
    );

    const [setDynamicDataMutation] = useMutation(SET_PROJECT_DYNAMIC_DATA);
    const [fetchPreviewContent] = useLazyQuery(GET_LIBRARY_PREVIEW_CONTENT);
    const [fetchPreviewVersions] = useLazyQuery(GET_VERSION_PREVIEW_DATA);

    const existingEntry = useMemo(() => {
        const entries: any[] = queryData?.Project?.dynamicData ?? [];
        return entries.find(
            d => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
        ) as { id: string; name: string; type: string; data?: string; setData?: string } | undefined;
    }, [queryData]);

    /* ── Load blob ──────────────────────────────────────────────── */
    useEffect(() => {
        if (queryLoading || !existingEntry?.data) return;
        setIsLoadingBlob(true);
        download(existingEntry.data)
            .then(raw => {
                const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
                const parsed = JSON.parse(str) as NomenclatureData;
                setData(parsed);
            })
            .catch(() => {})
            .finally(() => setIsLoadingBlob(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingEntry?.data, queryLoading]);

    /* ── Save ───────────────────────────────────────────────────── */
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveStatus('idle');
        try {
            if (!existingEntry) {
                await setDynamicDataMutation({
                    variables: {
                        projectId,
                        data: {
                            name: DYNAMIC_DATA_NAME,
                            type: DYNAMIC_DATA_TYPE,
                            description: 'Nomenclature pattern and mappings for Revit family renaming',
                        },
                    },
                });
            }
            const refetched = await refetch();
            const entry: any = (refetched.data?.Project?.dynamicData ?? []).find(
                (d: any) => d?.type === DYNAMIC_DATA_TYPE && d?.name === DYNAMIC_DATA_NAME,
            );
            if (!entry?.setData) throw new Error('No upload URL returned');
            await httpClient.put(entry.setData, JSON.stringify(data), {
                headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json', 'Cache-Control': 'no-cache, no-store, must-revalidate' },
            });
            setIsDirty(false);
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 4000);
        } finally {
            setIsSaving(false);
        }
    }, [data, existingEntry, projectId, setDynamicDataMutation, httpClient, refetch]);

    /* ── Navigation guard ───────────────────────────────────────── */
    const handleBack = useCallback(() => {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        onBack();
    }, [isDirty, onBack]);

    /* ── Pattern change ─────────────────────────────────────────── */
    const handlePatternChange = useCallback((newPattern: string) => {
        setData(prev => {
            const newMappedNames = getMappedTokenNames(newPattern);
            const newMappings = { ...prev.Mappings };
            for (const name of newMappedNames) {
                if (!newMappings[name]) newMappings[name] = { Values: {} };
            }
            return { ...prev, Pattern: newPattern, Mappings: newMappings };
        });
        setIsDirty(true);
    }, []);

    /* ── Mapping mutations ──────────────────────────────────────── */
    const setMappingValue = useCallback((mapping: string, original: string, mapped: string) => {
        setData(prev => ({
            ...prev,
            Mappings: {
                ...prev.Mappings,
                [mapping]: {
                    Values: { ...(prev.Mappings[mapping]?.Values ?? {}), [original]: mapped },
                },
            },
        }));
        setIsDirty(true);
    }, []);

    const renameMappingKey = useCallback((mapping: string, oldKey: string, newKey: string) => {
        if (!newKey || newKey === oldKey) return;
        setData(prev => {
            const values = { ...(prev.Mappings[mapping]?.Values ?? {}) };
            const existing = values[oldKey] ?? '';
            delete values[oldKey];
            values[newKey] = existing;
            return { ...prev, Mappings: { ...prev.Mappings, [mapping]: { Values: values } } };
        });
        setIsDirty(true);
    }, []);

    const removeMappingRow = useCallback((mapping: string, original: string) => {
        setData(prev => {
            const values = { ...(prev.Mappings[mapping]?.Values ?? {}) };
            delete values[original];
            return { ...prev, Mappings: { ...prev.Mappings, [mapping]: { Values: values } } };
        });
        setIsDirty(true);
    }, []);

    const handlePasteImport = useCallback((mapping: string, entries: PasteEntry[]) => {
        setData(prev => {
            const values = { ...(prev.Mappings[mapping]?.Values ?? {}) };
            for (const { original, mapped } of entries) values[original] = mapped;
            return { ...prev, Mappings: { ...prev.Mappings, [mapping]: { Values: values } } };
        });
        setIsDirty(true);
    }, []);

    const loadPreview = useCallback(async () => {
        if (!libraryId) return;
        setPreviewLoading(true);
        try {
            const contentResult = await fetchPreviewContent({ variables: { libraryId } });
            const nodes = (contentResult.data?.Library?.contents?.nodes ?? []) as any[];
            const versionIds = nodes.map((n: any) => n?.currentVersionId).filter(Boolean);
            const versionResult = await fetchPreviewVersions({ variables: { ids: versionIds } });
            const versions = (versionResult.data?.ContentVersionList?.nodes ?? []) as any[];
            const versionMap = new Map(versions.map((v: any) => [v.id, v]));
            const items: RawPreviewItem[] = nodes.map((n: any) => {
                const ver = versionMap.get(n?.currentVersionId);
                // Parse settings JSON for any additional properties
                let settingsProps: Record<string, string> = {};
                if (ver?.settings) {
                    try {
                        const parsed = JSON.parse(ver.settings);
                        for (const [k, v] of Object.entries(parsed)) {
                            if (typeof v === 'string') settingsProps[k] = v;
                        }
                    } catch { /* ignore */ }
                }
                return {
                    contentName: n?.name ?? '',
                    properties: {
                        Name: n?.name ?? '',
                        Category: ver?.category ?? '',
                        SubCategory: ver?.subCategory ?? '',
                        ...settingsProps,
                    },
                };
            });
            setRawPreviewItems(items);
        } finally {
            setPreviewLoading(false);
        }
    }, [libraryId, fetchPreviewContent, fetchPreviewVersions]);

    /* ── Commit inline cell edit ────────────────────────────────── */
    const commitEdit = useCallback(() => {
        if (!editingCell) return;
        const { mapping, key, field, draft } = editingCell;
        if (field === 'value') {
            setMappingValue(mapping, key, draft);
        } else {
            renameMappingKey(mapping, key, draft.trim());
        }
        setEditingCell(null);
    }, [editingCell, setMappingValue, renameMappingKey]);

    /* ── Derived ────────────────────────────────────────────────── */
    const tokens = useMemo(() => parsePattern(data.Pattern), [data.Pattern]);
    const mappedTokenNames = useMemo(() => getMappedTokenNames(data.Pattern), [data.Pattern]);
    const previewRows = useMemo(
        () => rawPreviewItems.map(item => ({
            contentName: item.contentName,
            ...applyNomenclature(tokens, item.properties, data.Mappings),
        })),
        [rawPreviewItems, tokens, data.Mappings],
    );
    const orphanMappingNames = useMemo(
        () => Object.keys(data.Mappings).filter(k => !mappedTokenNames.includes(k)),
        [data.Mappings, mappedTokenNames],
    );
    const isLoading = queryLoading || isLoadingBlob;

    /* ── Mapping table renderer ─────────────────────────────────── */
    const renderMappingSection = (mappingName: string, isOrphan = false) => {
        const values = data.Mappings[mappingName]?.Values ?? {};
        const allEntries = Object.entries(values);
        const search = searchFor[mappingName] ?? '';
        const filtered = search
            ? allEntries.filter(([k, v]) =>
                k.toLowerCase().includes(search.toLowerCase()) ||
                v.toLowerCase().includes(search.toLowerCase()))
            : allEntries;

        return (
            <div
                key={mappingName}
                className="card"
                style={{
                    border: `1px solid ${isOrphan ? 'var(--warning, #b45309)' : 'var(--border-default)'}`,
                    overflow: 'hidden',
                }}
            >
                {/* Section header */}
                <div
                    className="flex items-center gap-2 px-4 py-2.5 border-b"
                    style={{
                        borderColor: 'var(--border-default)',
                        backgroundColor: isOrphan
                            ? 'var(--warning-soft, #fff8e9)'
                            : 'var(--surface-1, var(--surface-100))',
                    }}
                >
                    <span
                        className="text-xs font-mono px-1.5 py-0.5 rounded font-semibold"
                        style={{
                            backgroundColor: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
                        }}
                    >
                        [{mappingName}]
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        {allEntries.length} row{allEntries.length !== 1 ? 's' : ''}
                    </span>
                    {isOrphan && (
                        <span className="text-xs flex items-center gap-1" style={{ color: 'var(--warning, #b45309)' }}>
                            <Info size={11} /> not in pattern
                        </span>
                    )}
                    <div className="flex-1" />
                    {allEntries.length > 8 && (
                        <input
                            type="text"
                            className="input-base"
                            style={{ width: 160, fontSize: 12, padding: '3px 8px' }}
                            placeholder="Filter rows…"
                            value={search}
                            onChange={e => setSearchFor(prev => ({ ...prev, [mappingName]: e.target.value }))}
                        />
                    )}
                    <button
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-md"
                        style={{
                            background: 'none',
                            border: '1px solid var(--border-default)',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                        }}
                        onClick={() => setPasteModalFor(mappingName)}
                    >
                        <ClipboardList size={11} /> Paste
                    </button>
                    <button
                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-md"
                        style={{
                            background: 'none',
                            border: '1px solid var(--border-default)',
                            color: 'var(--text-secondary)',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            setAddingRowFor(mappingName);
                            setNewRowOriginal('');
                            setNewRowMapped('');
                        }}
                    >
                        <Plus size={11} /> Add Row
                    </button>
                </div>

                {/* Table */}
                {(filtered.length > 0 || addingRowFor === mappingName) && (
                    <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{
                                    position: 'sticky', top: 0, zIndex: 1,
                                    backgroundColor: 'var(--surface-1, var(--surface-100))',
                                }}>
                                    <th style={{
                                        padding: '6px 12px', textAlign: 'left', fontSize: 11,
                                        color: 'var(--text-tertiary)', fontWeight: 600, width: '46%',
                                        borderBottom: '1px solid var(--border-default)',
                                    }}>
                                        Original
                                    </th>
                                    <th style={{
                                        padding: '6px 4px', width: 20,
                                        borderBottom: '1px solid var(--border-default)',
                                    }} />
                                    <th style={{
                                        padding: '6px 12px', textAlign: 'left', fontSize: 11,
                                        color: 'var(--text-tertiary)', fontWeight: 600,
                                        borderBottom: '1px solid var(--border-default)',
                                    }}>
                                        Mapped To
                                    </th>
                                    <th style={{ width: 36, borderBottom: '1px solid var(--border-default)' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(([original, mapped]) => {
                                    const isEditKey = editingCell?.mapping === mappingName &&
                                        editingCell.key === original && editingCell.field === 'key';
                                    const isEditVal = editingCell?.mapping === mappingName &&
                                        editingCell.key === original && editingCell.field === 'value';
                                    return (
                                        <tr
                                            key={original}
                                            className="group"
                                            style={{ borderTop: '1px solid var(--border-subtle)' }}
                                        >
                                            {/* Original cell */}
                                            <td style={{ padding: '3px 12px' }}>
                                                {isEditKey ? (
                                                    <input
                                                        autoFocus
                                                        className="input-base w-full"
                                                        style={{ fontSize: 13, padding: '2px 6px' }}
                                                        value={editingCell!.draft}
                                                        onChange={e => setEditingCell(p => p ? { ...p, draft: e.target.value } : null)}
                                                        onBlur={commitEdit}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                                                            if (e.key === 'Escape') setEditingCell(null);
                                                            if (e.key === 'Tab') { e.preventDefault(); commitEdit(); setEditingCell({ mapping: mappingName, key: editingCell!.draft || original, field: 'value', draft: mapped }); }
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        className="cursor-text block rounded"
                                                        style={{ color: 'var(--text-primary)', padding: '2px 4px', minHeight: 22 }}
                                                        onClick={() => setEditingCell({ mapping: mappingName, key: original, field: 'key', draft: original })}
                                                        title="Click to edit"
                                                    >
                                                        {original}
                                                    </span>
                                                )}
                                            </td>
                                            {/* Arrow */}
                                            <td style={{ padding: '3px 4px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
                                                →
                                            </td>
                                            {/* Mapped cell */}
                                            <td style={{ padding: '3px 12px' }}>
                                                {isEditVal ? (
                                                    <input
                                                        autoFocus
                                                        className="input-base w-full"
                                                        style={{ fontSize: 13, padding: '2px 6px' }}
                                                        value={editingCell!.draft}
                                                        onChange={e => setEditingCell(p => p ? { ...p, draft: e.target.value } : null)}
                                                        onBlur={commitEdit}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                                                            if (e.key === 'Escape') setEditingCell(null);
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        className="cursor-text block rounded"
                                                        style={{ color: mapped ? 'var(--text-secondary)' : 'var(--text-tertiary)', fontStyle: mapped ? 'normal' : 'italic', padding: '2px 4px', minHeight: 22 }}
                                                        onClick={() => setEditingCell({ mapping: mappingName, key: original, field: 'value', draft: mapped })}
                                                        title="Click to edit"
                                                    >
                                                        {mapped || 'empty'}
                                                    </span>
                                                )}
                                            </td>
                                            {/* Delete */}
                                            <td style={{ padding: '3px 8px', textAlign: 'center' }}>
                                                <button
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger, #ef4444)', padding: 2 }}
                                                    onClick={() => removeMappingRow(mappingName, original)}
                                                    title="Remove row"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* Add row */}
                                {addingRowFor === mappingName && (
                                    <tr style={{ borderTop: '1px solid var(--border-subtle)', backgroundColor: 'var(--accent-soft)' }}>
                                        <td style={{ padding: '6px 12px' }}>
                                            <input
                                                autoFocus
                                                className="input-base w-full"
                                                style={{ fontSize: 13, padding: '3px 6px' }}
                                                placeholder="Original value"
                                                value={newRowOriginal}
                                                onChange={e => setNewRowOriginal(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Tab') {
                                                        e.preventDefault();
                                                        (e.currentTarget
                                                            .closest('tr')
                                                            ?.querySelectorAll('input')[1] as HTMLInputElement | undefined)
                                                            ?.focus();
                                                    }
                                                    if (e.key === 'Escape') setAddingRowFor(null);
                                                }}
                                            />
                                        </td>
                                        <td style={{ padding: '6px 4px', color: 'var(--text-tertiary)', textAlign: 'center' }}>→</td>
                                        <td style={{ padding: '6px 12px' }}>
                                            <input
                                                className="input-base w-full"
                                                style={{ fontSize: 13, padding: '3px 6px' }}
                                                placeholder="Mapped value"
                                                value={newRowMapped}
                                                onChange={e => setNewRowMapped(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' && newRowOriginal.trim()) {
                                                        setMappingValue(mappingName, newRowOriginal.trim(), newRowMapped.trim());
                                                        setNewRowOriginal('');
                                                        setNewRowMapped('');
                                                        setAddingRowFor(null);
                                                    }
                                                    if (e.key === 'Escape') setAddingRowFor(null);
                                                }}
                                            />
                                        </td>
                                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                            <button
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                    color: 'var(--accent)', padding: 2,
                                                    opacity: newRowOriginal.trim() ? 1 : 0.4,
                                                }}
                                                disabled={!newRowOriginal.trim()}
                                                onClick={() => {
                                                    if (!newRowOriginal.trim()) return;
                                                    setMappingValue(mappingName, newRowOriginal.trim(), newRowMapped.trim());
                                                    setNewRowOriginal('');
                                                    setNewRowMapped('');
                                                    setAddingRowFor(null);
                                                }}
                                            >
                                                <Check size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Empty state */}
                {filtered.length === 0 && addingRowFor !== mappingName && (
                    <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            {search ? 'No rows match filter' : 'No mappings yet'}
                        </p>
                        {!search && (
                            <button
                                className="text-xs font-medium"
                                style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
                                onClick={() => {
                                    setAddingRowFor(mappingName);
                                    setNewRowOriginal('');
                                    setNewRowMapped('');
                                }}
                            >
                                + Add first mapping
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    /* ── Render ─────────────────────────────────────────────────── */
    return (
        <div className="flex flex-col h-full" style={style} data-testid="NomenclatureStandard">

            {/* Toolbar */}
            <div
                className="flex items-center gap-3 px-4 py-2.5 border-b flex-shrink-0"
                style={{ borderColor: 'var(--border-default)' }}
            >
                <button
                    className="flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md"
                    style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={handleBack}
                >
                    <ArrowLeft size={15} /> Back
                </button>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Nomenclature
                </span>
                <div className="flex-1" />
                {saveStatus === 'saved' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--success, #16a34a)' }}>
                        <Check size={13} /> Saved
                    </span>
                )}
                {saveStatus === 'error' && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--danger)' }}>
                        <AlertCircle size={13} /> Save failed
                    </span>
                )}
                {isDirty && saveStatus === 'idle' && (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Unsaved changes</span>
                )}
                <button
                    className="btn-primary flex items-center gap-2 text-sm"
                    disabled={!isDirty || isSaving}
                    onClick={handleSave}
                    style={{ opacity: !isDirty || isSaving ? 0.5 : 1 }}
                >
                    {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                </button>
            </div>

            {/* Content */}
            {isLoading ? (
                <div className="flex items-center justify-center flex-1">
                    <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                </div>
            ) : (
                <div className="flex-1 overflow-auto px-6 py-5 space-y-5">

                    {/* Pattern card */}
                    <div
                        className="card"
                        style={{ border: '1px solid var(--border-default)', overflow: 'hidden' }}
                    >
                        <div
                            className="flex items-center gap-2 px-4 py-2.5 border-b"
                            style={{
                                borderColor: 'var(--border-default)',
                                backgroundColor: 'var(--surface-1, var(--surface-100))',
                            }}
                        >
                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                Pattern
                            </span>
                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                — defines how Revit family names are constructed
                            </span>
                        </div>
                        <div className="p-4 space-y-3">
                            <input
                                type="text"
                                className="input-base w-full"
                                style={{ fontFamily: 'monospace' }}
                                placeholder="e.g. [Name]  or  [Category]_{Name}"
                                value={data.Pattern}
                                onChange={e => handlePatternChange(e.target.value)}
                            />

                            {/* Visual token preview */}
                            {tokens.length > 0 && (
                                <div className="flex items-center flex-wrap gap-1.5 min-h-7">
                                    {tokens.map((token, i) => {
                                        if (token.type === 'literal') {
                                            return (
                                                <span
                                                    key={i}
                                                    className="text-sm font-mono"
                                                    style={{ color: 'var(--text-secondary)' }}
                                                >
                                                    {token.text}
                                                </span>
                                            );
                                        }
                                        if (token.type === 'mapped') {
                                            return (
                                                <span
                                                    key={i}
                                                    className="text-xs font-mono px-2 py-0.5 rounded font-semibold"
                                                    style={{
                                                        backgroundColor: 'var(--accent-soft)',
                                                        color: 'var(--accent)',
                                                        border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
                                                    }}
                                                    title={`Mapped — looks up "${token.name}" value in the table below`}
                                                >
                                                    [{token.name}]
                                                </span>
                                            );
                                        }
                                        return (
                                            <span
                                                key={i}
                                                className="text-xs font-mono px-2 py-0.5 rounded font-semibold"
                                                style={{
                                                    backgroundColor: 'var(--info-soft)',
                                                    color: 'var(--info)',
                                                    border: '1px solid color-mix(in srgb, var(--info) 25%, transparent)',
                                                }}
                                                title={`Direct — reads "${token.name}" property value as-is`}
                                            >
                                                {'{' + token.name + '}'}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Legend */}
                            <div className="flex flex-wrap items-center gap-4 pt-1">
                                <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                    <span
                                        className="font-mono px-1 rounded"
                                        style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                                    >
                                        [&nbsp;]
                                    </span>
                                    Mapped — value translated via the table below
                                </div>
                                <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                    <span
                                        className="font-mono px-1 rounded"
                                        style={{ backgroundColor: 'var(--info-soft)', color: 'var(--info)' }}
                                    >
                                        {'{ }'}
                                    </span>
                                    Direct — Revit property value used as-is
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* One mapping section per [token] in pattern */}
                    {mappedTokenNames.map(name => renderMappingSection(name))}

                    {/* No mapped tokens in pattern */}
                    {mappedTokenNames.length === 0 && data.Pattern.trim() && (
                        <div className="flex flex-col items-center justify-center py-10 gap-2">
                            <Info size={24} style={{ color: 'var(--text-tertiary)', opacity: 0.4 }} />
                            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                                No <code>[mapped]</code> tokens in the pattern — no lookup tables needed.
                            </p>
                        </div>
                    )}

                    {/* Orphan mappings (exist in stored data but not in current pattern) */}
                    {orphanMappingNames.length > 0 && (
                        <div className="space-y-3">
                            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                The following mapping tables exist but are not referenced in the current pattern:
                            </p>
                            {orphanMappingNames.map(name => renderMappingSection(name, true))}
                        </div>
                    )}

                    {/* Preview */}
                    {libraryId && (
                        <div
                            className="card"
                            style={{ border: '1px solid var(--border-default)', overflow: 'hidden' }}
                        >
                            {/* Collapsible header */}
                            <div
                                className="flex items-center gap-2 px-4 py-2.5 cursor-pointer select-none"
                                style={{ backgroundColor: 'var(--surface-1, var(--surface-100))' }}
                                onClick={() => {
                                    if (!previewOpen) {
                                        setPreviewOpen(true);
                                        if (rawPreviewItems.length === 0) loadPreview();
                                    } else {
                                        setPreviewOpen(false);
                                    }
                                }}
                            >
                                <span className="text-sm font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                                    Preview
                                </span>
                                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                    5 recent library items
                                </span>
                                {previewOpen && (
                                    <button
                                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-md"
                                        style={{
                                            background: 'none',
                                            border: '1px solid var(--border-default)',
                                            color: 'var(--text-secondary)',
                                            cursor: 'pointer',
                                        }}
                                        onClick={e => { e.stopPropagation(); loadPreview(); }}
                                    >
                                        <RefreshCw size={11} /> Refresh
                                    </button>
                                )}
                                {previewOpen
                                    ? <ChevronDown size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                                    : <ChevronRight size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />}
                            </div>

                            {/* Content */}
                            {previewOpen && (
                                <div className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    {previewLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                                        </div>
                                    ) : previewRows.length === 0 ? (
                                        <div className="flex items-center justify-center py-8">
                                            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No content found in library</p>
                                        </div>
                                    ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ backgroundColor: 'var(--surface-1, var(--surface-100))' }}>
                                                    <th style={{ padding: '6px 12px', textAlign: 'left', fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600, width: '45%', borderBottom: '1px solid var(--border-default)' }}>Original Name</th>
                                                    <th style={{ padding: '6px 4px', width: 20, borderBottom: '1px solid var(--border-default)' }} />
                                                    <th style={{ padding: '6px 12px', textAlign: 'left', fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 600, borderBottom: '1px solid var(--border-default)' }}>Proposed Name</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewRows.map((row, i) => (
                                                    <tr key={i} style={{ borderTop: i > 0 ? '1px solid var(--border-subtle)' : undefined }}>
                                                        <td style={{ padding: '6px 12px', color: 'var(--text-secondary)' }}>
                                                            {row.contentName}
                                                        </td>
                                                        <td style={{ padding: '6px 4px', color: 'var(--text-tertiary)', textAlign: 'center' }}>→</td>
                                                        <td style={{ padding: '6px 12px' }}>
                                                            {row.segments.map((seg, j) => (
                                                                <span
                                                                    key={j}
                                                                    title={seg.kind === 'mapped' ? 'Mapped token' : seg.kind === 'direct' ? 'Direct token' : seg.kind === 'miss' ? 'Property not found' : undefined}
                                                                    style={{
                                                                        color: seg.kind === 'miss' ? 'var(--danger, #ef4444)'
                                                                            : seg.kind === 'mapped' ? 'var(--accent)'
                                                                            : seg.kind === 'direct' ? 'var(--info)'
                                                                            : 'var(--text-primary)',
                                                                        fontStyle: seg.kind === 'miss' ? 'italic' : 'normal',
                                                                    }}
                                                                >
                                                                    {seg.text}
                                                                </span>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    {/* Legend */}
                                    {previewRows.length > 0 && (
                                        <div className="flex flex-wrap gap-3 px-4 py-2 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                            <span className="text-xs" style={{ color: 'var(--accent)' }}>● Mapped</span>
                                            <span className="text-xs" style={{ color: 'var(--info)' }}>● Direct</span>
                                            <span className="text-xs" style={{ color: 'var(--danger, #ef4444)', fontStyle: 'italic' }}>● Not found</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            )}

            {/* Paste modal */}
            {pasteModalFor && (
                <MappingPasteModal
                    tokenName={pasteModalFor}
                    existingValues={data.Mappings[pasteModalFor]?.Values ?? {}}
                    onImport={entries => handlePasteImport(pasteModalFor, entries)}
                    onClose={() => setPasteModalFor(null)}
                />
            )}
        </div>
    );
};

export default NomenclatureStandard;
